// ConvoCX unified inbox — conversation queue (CON-845, multi-select filters CON-853).
// Left pane: search, multi-select status/channel dropdowns, and the list.

import {
  Avatar,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  TextInput,
} from '@astryxdesign/core';
import type { Conversation } from './types';
import type { InboxFilters } from './useInbox';

const CHANNELS: Array<{ id: string; label: string }> = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'messenger', label: 'Messenger' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'web', label: 'Web chat' },
  { id: 'telegram', label: 'Telegram' },
];

const STATUS_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'open', label: 'Open' },
  { id: 'pending', label: 'Pending' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'snoozed', label: 'Snoozed' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'assigned-me', label: 'Assigned to me' },
];

// Compact multi-select filter: a dropdown of checkboxes that stays open while
// several options are toggled, replacing the previous segmented control + button
// row (which consumed a lot of horizontal space). An empty selection = all.
function FilterDropdown({
  label,
  allLabel,
  testId,
  options,
  selected,
  onChange,
}: {
  label: string;
  allLabel: string;
  testId: string;
  options: Array<{ id: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const count = selected.length;
  const toggle = (id: string, checked: boolean) => {
    onChange(checked ? [...selected, id] : selected.filter((s) => s !== id));
  };
  return (
    <DropdownMenu
      data-testid={testId}
      menuWidth={220}
      button={{
        label: count ? `${label} (${count})` : label,
        variant: count ? 'primary' : 'ghost',
        size: 'sm',
        endContent: count ? <Badge label={String(count)} variant="neutral" /> : undefined,
      }}
    >
      <DropdownMenuCheckboxItem
        label={allLabel}
        value={count === 0}
        onChange={(checked) => {
          if (checked) onChange([]);
        }}
      />
      {options.map((o) => (
        <DropdownMenuCheckboxItem
          key={o.id}
          label={o.label}
          value={selected.includes(o.id)}
          onChange={(checked) => toggle(o.id, checked)}
        />
      ))}
    </DropdownMenu>
  );
}

function channelLabel(id: string | undefined | null): string {
  if (!id) return 'Conversation';
  const ch = CHANNELS.find((c) => c.id === id);
  return ch?.label ?? id;
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  const now = Date.now();
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return `${mins}m`;
  const days = Math.floor(diff / 86400000);
  if (days < 1) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString();
}

function participantName(c: Conversation): string {
  const p = (c.participant_data ?? {}) as { name?: string; email?: string; phone?: string };
  return p.name || p.email || c.channel_name || c.subject || `Conversation #${c.id.slice(0, 6)}`;
}

interface QueueProps {
  conversations: Conversation[];
  loading: boolean;
  error: string | null;
  selectedId: string | null;
  filters: InboxFilters;
  onFiltersChange: (f: InboxFilters) => void;
  onSelect: (id: string) => void;
  onRetry: () => void;
}

export function ConversationQueue({
  conversations,
  loading,
  error,
  selectedId,
  filters,
  onFiltersChange,
  onSelect,
  onRetry,
}: QueueProps) {
  return (
    <div className="ccx-inbox__queue" data-testid="inbox-queue">
      <div className="ccx-queue__search">
        <TextInput
          label="Search conversations"
          isLabelHidden
          placeholder="Search conversations…"
          value={filters.search}
          onChange={(value) => onFiltersChange({ ...filters, search: value })}
        />
      </div>

      <div className="ccx-queue__filters">
        <FilterDropdown
          label="Status"
          allLabel="All statuses"
          testId="inbox-status-filter"
          options={STATUS_OPTIONS}
          selected={filters.statuses}
          onChange={(statuses) => onFiltersChange({ ...filters, statuses })}
        />
        <FilterDropdown
          label="Channel"
          allLabel="All channels"
          testId="inbox-channel-filter"
          options={CHANNELS}
          selected={filters.channels}
          onChange={(channels) => onFiltersChange({ ...filters, channels })}
        />
      </div>

      <div className="ccx-queue__list-head" data-testid="inbox-queue-list-head">
        <span className="ccx-queue__list-title">All conversations</span>
        {!loading && !error && conversations.length > 0 && <Badge label={String(conversations.length)} variant="neutral" />}
      </div>

      <div className="ccx-queue__rows" data-testid="inbox-queue-rows">
        {error && (
          <div className="ccx-queue__state">
            <Badge label="Failed to load" variant="error" />
            <Button label="Retry" variant="ghost" size="sm" onClick={onRetry} />
          </div>
        )}

        {loading && conversations.length === 0 && (
          <div className="ccx-queue__state">Loading conversations…</div>
        )}

        {!loading && !error && conversations.length === 0 && (
          <div className="ccx-queue__state">No conversations match this filter.</div>
        )}

        {conversations.map((c) => {
          const unread = c.unread_count ?? 0;
          const subject = (c.subject ?? '').trim();
          const preview = (c.last_message_preview ?? '').trim();
          const showSubject = subject !== '' && subject !== preview;
          return (
            <div
              key={c.id}
              className={`ccx-queue__row${selectedId === c.id ? ' is-active' : ''}${unread > 0 ? ' is-unread' : ''}`}
              onClick={() => onSelect(c.id)}
              data-testid="inbox-queue-row"
              role="button"
              tabIndex={0}
              aria-selected={selectedId === c.id}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(c.id);
                }
              }}
            >
              <div className="ccx-queue__row-avatar">
                <Avatar name={participantName(c)} size="md" />
              </div>
              <div className="ccx-queue__row-body">
                <div className="ccx-queue__row-top">
                  <span className="ccx-queue__row-name">{participantName(c)}</span>
                  <span className="ccx-queue__row-time">{formatTime(c.last_message_at)}</span>
                </div>
                {showSubject && <div className="ccx-queue__row-subject">{subject}</div>}
                <div className="ccx-queue__row-preview">
                  <span
                    className="ccx-channel-dot"
                    style={{ background: `var(--color-channel-${c.channel_platform ?? 'web'}, #7c5cfc)` }}
                  />
                  <span className="ccx-queue__row-preview-text">
                    {preview || channelLabel(c.channel_platform) || 'No messages yet'}
                    {c.assigned_to_name ? ` · ${c.assigned_to_name}` : ''}
                  </span>
                </div>
                {(c.labels?.length ?? 0) > 0 && (
                  <div className="ccx-queue__row-labels">
                    {c.labels!.map((l) => (
                      <span key={l.id} className="ccx-queue__label" style={{ background: l.color || 'var(--color-accent, #7c5cfc)' }}>
                        {l.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {unread > 0 && (
                <span className="ccx-queue__row-right" title={`${unread} unread`}>
                  <span className="ccx-queue__unread" data-testid="queue-unread-count">{unread > 99 ? '99+' : unread}</span>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}